import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore,
  VideoFile,
  Settings,
  Speed,
  HighQuality
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../services/api';
import { invalidateMediaData } from '../../utils/cacheInvalidation';

interface ConversionDialogProps {
  open: boolean;
  onClose: () => void;
  media: any;
  onConversionStart?: (jobId: string) => void;
}

interface ConversionProfile {
  name: string;
  description: string;
  format: string;
  video_codec?: string;
  audio_codec?: string;
  width?: number;
  height?: number;
  bitrate?: number;
  quality?: string;
}

const ConversionDialog: React.FC<ConversionDialogProps> = ({
  open,
  onClose,
  media,
  onConversionStart
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Защита от null media
  if (!media) {
    return null;
  }

  const [profiles, setProfiles] = useState<Record<string, ConversionProfile>>({});
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Загружаем профили конвертации
  useEffect(() => {
    if (open) {
      loadProfiles();
    }
  }, [open]);

  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getConversionProfiles();
      setProfiles(response.profiles);

      // Выбираем первый профиль по умолчанию
      const profileNames = Object.keys(response.profiles);
      if (profileNames.length > 0) {
        setSelectedProfile(profileNames[0]);
      }
    } catch (error) {
      console.error('Ошибка загрузки профилей конвертации:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const conversionMutation = useMutation({
    mutationFn: (profileName: string) =>
      apiClient.convertMedia(media.id, profileName),
    onSuccess: (result) => {
      invalidateMediaData(queryClient);
      // Обновляем историю конвертаций
      if (onConversionStart) {
        onConversionStart(result.job_id);
      }
      // Показываем уведомление об успехе
      console.log('Конвертация запущена:', result);
      onClose();
    },
    onError: (error) => {
      console.error('Ошибка конвертации:', error);
    }
  });

  const handleConvert = () => {
    if (selectedProfile) {
      conversionMutation.mutate(selectedProfile);
    }
  };

  const getProfileIcon = (profileName: string) => {
    switch (profileName) {
      case 'high_quality':
        return <HighQuality color="primary" />;
      case 'web_optimized':
        return <VideoFile color="success" />;
      case 'mobile_friendly':
        return <Speed color="info" />;
      case 'small_size':
        return <Settings color="warning" />;
      default:
        return <VideoFile />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const estimateFileSize = (profile: ConversionProfile) => {
    if (!profile.bitrate || !media.duration) return 'N/A';
    const estimatedBytes = (profile.bitrate * 1000 * media.duration) / 8;
    return formatFileSize(estimatedBytes);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <VideoFile />
          {t('media.conversion.title')}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {media?.title || ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>

        {isLoading ? (
          <Box sx={{ width: '100%', my: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('media.conversion.loadingProfiles')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('media.conversion.selectProfile')}</InputLabel>
              <Select
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
                label={t('media.conversion.selectProfile')}
              >
                {Object.entries(profiles).map(([key, profile]) => (
                  <MenuItem key={key} value={key}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getProfileIcon(key)}
                      {t(`media.conversion.profiles.${key}`, profile.description)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedProfile && profiles[selectedProfile] && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t(`media.conversion.profiles.${selectedProfile}`, profiles[selectedProfile].description)}
                  </Typography>

                                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="primary">
                        {t('media.conversion.videoSettings')}
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          <strong>{t('media.format')}:</strong> {profiles[selectedProfile].format.toUpperCase()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('media.codec')}:</strong> {profiles[selectedProfile].video_codec || 'Auto'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('media.resolution')}:</strong> {
                            profiles[selectedProfile].width && profiles[selectedProfile].height
                              ? `${profiles[selectedProfile].width}x${profiles[selectedProfile].height}`
                              : 'Original'
                          }
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('media.bitrate')}:</strong> {profiles[selectedProfile].bitrate ? `${profiles[selectedProfile].bitrate} kbps` : 'Auto'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" component="span">
                            <strong>{t('media.quality')}:</strong>
                          </Typography>
                          <Chip
                            label={t(`media.conversion.quality.${profiles[selectedProfile].quality}`, profiles[selectedProfile].quality)}
                            size="small"
                            color={
                              profiles[selectedProfile].quality === 'high' ? 'success' :
                              profiles[selectedProfile].quality === 'medium' ? 'primary' :
                              profiles[selectedProfile].quality === 'low' ? 'warning' : 'default'
                            }
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" color="primary">
                        {t('media.conversion.estimates')}
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          <strong>{t('media.conversion.estimatedSize')}:</strong> {estimateFileSize(profiles[selectedProfile])}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('media.conversion.estimatedTime')}:</strong> {
                            media.duration ? `${Math.ceil(media.duration / 60)} min` : 'N/A'
                          }
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('media.audioCodec')}:</strong> {profiles[selectedProfile].audio_codec || 'Auto'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  {t('media.conversion.originalFileInfo')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2">
                      <strong>{t('media.format')}:</strong> {media.type?.toUpperCase() || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('media.fileSize')}:</strong> {formatFileSize(media.file_size)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2">
                      <strong>{t('media.resolution')}:</strong> {
                        media.width && media.height ? `${media.width}x${media.height}` : 'N/A'
                      }
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('common.duration')}:</strong> {
                        media.duration ? `${Math.floor(media.duration / 60)}:${Math.floor(media.duration % 60).toString().padStart(2, '0')}` : 'N/A'
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {conversionMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('media.conversion.error')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={conversionMutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConvert}
          variant="contained"
          disabled={!selectedProfile || conversionMutation.isPending || isLoading}
          startIcon={conversionMutation.isPending ? <LinearProgress size={20} /> : <VideoFile />}
        >
          {conversionMutation.isPending ? t('media.conversion.starting') : t('media.conversion.start')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversionDialog;
